import { useState } from "react";
import { MessageSquarePlus, X, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { trackEvent } from "@/lib/analytics";

const CATS = [
  { value: "content", label: "Improve the content" },
  { value: "market", label: "A community/market we should reach" },
  { value: "general", label: "General suggestion" },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("content");
  const [message, setMessage] = useState("");
  const [market, setMarket] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) { toast.error("Please share a little more detail"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", {
        category, message: message.trim(), market: market.trim() || null, email: email.trim() || null,
      });
      trackEvent("feedback_submitted", { category });
      toast.success(data.message || "Thank you for your feedback!");
      setMessage(""); setMarket(""); setEmail(""); setOpen(false);
    } catch {
      toast.error("Could not send feedback. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-open-button"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-surface border border-gold/30 text-bone/80 pl-3.5 pr-4 py-2.5 rounded-full shadow-lg shadow-black/40 hover:border-gold hover:text-gold transition-colors">
        <MessageSquarePlus size={18} strokeWidth={1.75} />
        <span className="text-sm hidden sm:inline">Share Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-md bg-surface border border-gold/25 shadow-2xl p-7 space-y-4" data-testid="feedback-form">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-gold">
                <Sparkles size={18} />
                <h2 className="font-serif text-2xl text-bone">Help us grow</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="feedback-close-button" className="text-bone/50 hover:text-gold"><X size={20} /></button>
            </div>
            <p className="text-bone/55 text-sm">Your voice shapes Apocalypz.org. Tell us how to improve the teachings — or which communities and regions we haven't reached yet.</p>

            <div>
              <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">What's this about?</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="feedback-category"
                className="w-full bg-black/50 border border-white/10 px-3.5 py-2.5 text-bone text-sm focus:outline-none focus:border-gold/50">
                {CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            {category === "market" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">Region / Community</label>
                <input value={market} onChange={(e) => setMarket(e.target.value)} data-testid="feedback-market"
                  placeholder="e.g. Trinidad, Ethiopia, French-speaking Africa, Atlanta..."
                  className="w-full bg-black/50 border border-white/10 px-3.5 py-2.5 text-bone text-sm focus:outline-none focus:border-gold/50" />
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">Your message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required data-testid="feedback-message"
                placeholder="Share your idea, suggestion, or what you'd love to see..."
                className="w-full bg-black/50 border border-white/10 px-3.5 py-2.5 text-bone text-sm focus:outline-none focus:border-gold/50" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">Email (optional — if you'd like a reply)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="feedback-email"
                className="w-full bg-black/50 border border-white/10 px-3.5 py-2.5 text-bone text-sm focus:outline-none focus:border-gold/50" />
            </div>

            <button type="submit" disabled={busy} data-testid="feedback-submit"
              className="w-full bg-gold text-black py-3 font-medium hover:bg-gold2 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send Feedback
            </button>
          </form>
        </div>
      )}
    </>
  );
}
